import React, { useMemo, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
type Expenses = {
  housing: number;
  transport: number;
  food: number;
  utilities: number;
  entertainment: number;
  savings: number;
  other: number;
};
const COLORS = ['#3b82f6', '#06b6d4', '#14b8a6', '#8b5cf6', '#f59e0b', '#10b981', '#64748b'];
export function BudgetPlanner() {
  const [income, setIncome] = useState<number>(8000);
  const [expenses, setExpenses] = useState<Expenses>({
    housing: 2500,
    transport: 600,
    food: 1200,
    utilities: 400,
    entertainment: 500,
    savings: 1500,
    other: 300
  });
  const handleExpenseChange = (key: keyof Expenses, value: number) => {
    setExpenses(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const results = useMemo(() => {
    const totalExpenses = Object.values(expenses).reduce((a, b) => a + b, 0);
    const remaining = income - totalExpenses;
    const chartData = [{
      name: 'Housing',
      value: expenses.housing
    }, {
      name: 'Transport',
      value: expenses.transport
    }, {
      name: 'Food',
      value: expenses.food
    }, {
      name: 'Utilities',
      value: expenses.utilities
    }, {
      name: 'Entertainment',
      value: expenses.entertainment
    }, {
      name: 'Savings',
      value: expenses.savings
    }, {
      name: 'Other',
      value: expenses.other
    }].filter(item => item.value > 0);
    return {
      totalExpenses,
      remaining,
      chartData
    };
  }, [income, expenses]);
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  }).format(val);
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-193">
      <div className="lg:col-span-1 space-y-6 bg-white/5 p-6 rounded-2xl border border-white/10 max-h-[700px] overflow-y-auto custom-scrollbar" data-id="element-194">
        <h3 className="text-xl font-semibold text-white mb-4" data-id="element-195">
          Monthly Budget
        </h3>

        <div data-id="element-196">
          <label className="block text-sm font-medium text-blue-400 mb-2" data-id="element-197">
            Monthly Income (After Tax)
          </label>
          <input type="number" value={income} onChange={e => setIncome(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-blue-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" data-id="element-198" />
        </div>

        <div className="pt-4 border-t border-white/10" data-id="element-199">
          <h4 className="text-sm font-medium text-slate-400 mb-4" data-id="element-200">Expenses</h4>

          {Object.entries(expenses).map(([key, value]) => <div key={key} className="mb-4" data-id="element-201">
              <label className="block text-sm font-medium text-slate-300 mb-1 capitalize" data-id="element-202">
                {key}
              </label>
              <input type="number" value={value} onChange={e => handleExpenseChange(key as keyof Expenses, Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all" data-id="element-203" />
            </div>)}
        </div>
      </div>

      <div className="lg:col-span-2 space-y-6" data-id="element-204">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-205">
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-206">
            <p className="text-sm text-slate-400 mb-1" data-id="element-207">Total Income</p>
            <p className="text-3xl font-bold text-white" data-id="element-208">
              {formatCurrency(income)}
            </p>
          </div>
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-209">
            <p className="text-sm text-slate-400 mb-1" data-id="element-210">Total Allocated</p>
            <p className="text-3xl font-bold text-cyan-400" data-id="element-211">
              {formatCurrency(results.totalExpenses)}
            </p>
          </div>
          <div className={`p-6 rounded-2xl border ${results.remaining >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`} data-id="element-212">
            <p className="text-sm text-slate-400 mb-1" data-id="element-213">Remaining</p>
            <p className={`text-3xl font-bold ${results.remaining >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-214">
              {formatCurrency(results.remaining)}
            </p>
          </div>
        </div>

        <div className="bg-white/5 p-6 rounded-2xl border border-white/10 h-[450px] flex flex-col items-center justify-center" data-id="element-215">
          <h4 className="text-sm font-medium text-slate-400 mb-2 w-full text-left" data-id="element-216">
            Allocation Breakdown
          </h4>
          {results.totalExpenses > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-217">
              <PieChart data-id="element-218">
                <Pie data={results.chartData} cx="50%" cy="50%" innerRadius={80} outerRadius={120} paddingAngle={5} dataKey="value" stroke="none" data-id="element-219">
                  {results.chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-220" />)}
                </Pie>
                <Tooltip contentStyle={{
              backgroundColor: '#0f172a',
              borderColor: '#1e293b',
              borderRadius: '8px',
              color: '#fff'
            }} formatter={(value: number) => formatCurrency(value)} data-id="element-221" />
                <Legend layout="vertical" verticalAlign="middle" align="right" data-id="element-222" />
              </PieChart>
            </ResponsiveContainer> : <div className="text-slate-500 flex items-center justify-center h-full" data-id="element-223">
              No expenses allocated
            </div>}
        </div>
      </div>
    </div>;
}